/**
 * The compare table: requirements down the left, quotes across, every cell scored.
 *
 * This is the screen the whole slice exists for. The Markel quote was the cheapest thing
 * on the table and also excluded the pool, capped water damage below a loss already
 * suffered twice, and depreciated a 22-year-old roof to nothing. Price sorts last here on
 * purpose — the dealbreaker count is the headline.
 *
 * It never recommends. It reports each quote against the checklist written down while
 * calm, and leaves the decision where it belongs.
 *
 * Two things the layout has to survive, both learned by testing it at 375px:
 *   * The header row sticks vertically as well as horizontally. Without it, scrolling to
 *     the fourth requirement loses the carrier and the price, and you no longer know
 *     which column you are reading.
 *   * The pinned requirement column carries its own right edge. Without it the previous
 *     column's clipped text slides underneath and the two read as one garbled column.
 */
import type { QuoteComparison } from '../../shared/types';
import { money } from '../../lib/api';
import {
  Chip,
  ScoreMark,
  cx,
  formatActual,
  isScored,
  requirementNeed,
  scoreWord,
  validityLabel,
  validityTone,
} from './ui';

const KIND_LABEL: Record<string, string> = {
  require: 'Must have',
  avoid: 'Must not have',
  prefer: 'Prefer',
};

export default function CompareTable({ comparison }: { comparison: QuoteComparison }) {
  const { requirements, columns } = comparison;
  const anyUnknown = columns.some((c) => c.unknown > 0);

  return (
    <div className="rounded-card border border-border bg-surface">
      {/*
        tabIndex + role make the scroller reachable by keyboard. The table holds no
        focusable elements at all, so without this a keyboard-only user cannot scroll
        right — with three quotes at 375px, columns two and three were simply
        unreachable. WCAG 2.1.1.
      */}
      <div
        className="max-h-[70vh] overflow-auto"
        tabIndex={0}
        role="region"
        aria-label={`${comparison.policy_type} quotes compared against your requirements`}
      >
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">
            Each quote scored against the household coverage requirements for{' '}
            {comparison.policy_type}. Requirements are rows; quotes are columns.
          </caption>

          <thead>
            <tr>
              <th
                scope="col"
                className="sticky top-0 left-0 z-20 w-40 min-w-[10rem] border-r border-b border-border bg-surface p-3 text-left align-bottom sm:w-52 sm:min-w-[13rem]"
              >
                <span className="text-xs font-semibold tracking-wide text-ink-soft uppercase">
                  Requirement
                </span>
              </th>

              {columns.map(({ quote, dealbreakers, unknown }) => {
                const label = validityLabel(quote.days_valid);
                return (
                  <th
                    key={quote.id}
                    scope="col"
                    className="sticky top-0 z-10 w-48 min-w-[8rem] border-b border-border bg-surface p-3 text-left align-bottom"
                  >
                    <span className="block font-display text-sm leading-snug font-semibold text-ink">
                      {quote.carrier}
                    </span>

                    <span className="mt-2 block text-base font-semibold text-ink tabular-nums">
                      {quote.total_cents === null ? '—' : money(quote.total_cents)}
                    </span>
                    {quote.fees_cents !== null && quote.fees_cents > 0 && (
                      <span className="block text-[11px] text-ink-soft tabular-nums">
                        incl. {money(quote.fees_cents)} fees
                      </span>
                    )}

                    <span className="mt-2 flex flex-wrap gap-1">
                      {label && <Chip tone={validityTone(quote.days_valid)}>{label}</Chip>}
                      {dealbreakers > 0 && (
                        <Chip tone="over">
                          {dealbreakers} dealbreaker{dealbreakers === 1 ? '' : 's'}
                        </Chip>
                      )}
                      {unknown > 0 && <Chip tone="neutral">{unknown} unanswered</Chip>}
                    </span>
                  </th>
                );
              })}

              {/* Absorbs leftover width so a single quote doesn't stretch to 1000px and
                  strand its score marks half a screen from the requirement they answer. */}
              <th aria-hidden="true" className="sticky top-0 z-10 w-full border-b border-border bg-surface" />
            </tr>
          </thead>

          <tbody>
            {requirements.map((req, rowIndex) => {
              const zebra = rowIndex % 2 === 1;
              const scored = isScored(req);
              return (
                <tr key={req.id} className={zebra ? 'bg-surface-sunk/40' : undefined}>
                  <th
                    scope="row"
                    className={cx(
                      'sticky left-0 z-10 border-r border-b border-border p-3 text-left align-top font-normal',
                      // Same alpha as the row, not a solid fill — a solid sticky cell read
                      // as a darker column and the row stopped looking like one row.
                      zebra ? 'bg-surface-sunk/40 backdrop-blur-none' : 'bg-surface'
                    )}
                  >
                    <span className="block text-sm leading-snug font-medium text-ink">
                      {req.label}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Chip tone={req.kind === 'prefer' ? 'neutral' : 'dusk'}>
                        {KIND_LABEL[req.kind] ?? req.kind}
                      </Chip>
                      {!scored && (
                        <span className="text-[11px] text-ink-soft">read the document</span>
                      )}
                    </span>
                  </th>

                  {columns.map((col) => {
                    const cell = col.cells.find((c) => c.requirement_id === req.id);
                    const score = cell?.score ?? 'unknown';
                    const severe = score === 'violated' && req.kind !== 'prefer';
                    const found = cell ? formatActual(req, cell.actual) : null;
                    const need = score === 'violated' ? requirementNeed(req) : null;

                    // Found value and the threshold it missed, together. The reader learns
                    // WHY it failed here, instead of tracking back across the table.
                    const detail = [found, need].filter(Boolean).join(' · ') || undefined;

                    return (
                      <td
                        key={col.quote.id}
                        className={cx(
                          'border-b border-border p-3 text-center align-top',
                          severe && 'bg-clay-soft/60'
                        )}
                        title={`${scoreWord(score)}${detail ? `: ${detail}` : ''}`}
                      >
                        <ScoreMark score={score} detail={detail} severe={severe} />
                      </td>
                    );
                  })}

                  <td aria-hidden="true" className="border-b border-border" />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {anyUnknown && (
        <p className="border-t border-border px-3 py-2.5 text-xs leading-relaxed text-ink-soft">
          A dash means the quote does not answer that requirement — a question to take back
          to the agent, never a pass.
        </p>
      )}
    </div>
  );
}
